<template>
    <div
        class="quick-search"
        :class="{ 'quick-search-compact': compact, 'quick-search-inline': inline }"
        v-if="visibleRows.length"
    >
        <div class="quick-block" v-for="row in visibleRows" :key="row.key">
            <p class="quick-title">{{ t(row.label) }}</p>
            <div class="quick-chips">
                <template v-for="item in shown(row)">
                    <!-- Suggestions are free text, so they stay buttons: an uncurated keyword URL
                         renders for a human but 410s for a crawler, and a real href would publish a
                         crawlable link into one. -->
                    <button
                        v-if="row.key === 'suggestions'"
                        :key="'s-' + item.label"
                        type="button"
                        class="quick-chip"
                        @click="$emit('select', item.label)"
                    >
                        <span class="quick-chip-label">{{ item.label }}</span>
                    </button>
                    <a
                        v-else
                        :key="row.key + '-' + item.url"
                        :href="isSelected(row, item) ? clearUrl : item.url"
                        class="quick-chip"
                        :class="{ 'is-selected': isSelected(row, item) }"
                        :title="isSelected(row, item) ? t('clear_filter') : item.label"
                    >
                        <span class="quick-chip-label">{{ item.label }}</span>
                        <span class="quick-chip-count" v-if="item.count">{{ item.count }}</span>
                        <span class="quick-chip-x" v-if="isSelected(row, item)" aria-hidden="true">&times;</span>
                    </a>
                </template>
                <button
                    type="button"
                    class="quick-more"
                    v-if="row.items.length > limit"
                    @click="toggle(row.key)"
                >
                    {{ expanded[row.key] ? t('show_less') : t('show_more').replace('{n}', row.items.length - limit) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
// Quick-search rows for the PT listing sidebar and the homepage.
//
// These are real <a> links on purpose: the existing "People also searched" chips are click-handlers,
// so they give crawlers no path at all. Every URL here comes from /api/quick-links, which only ever
// returns CURATED combo pages and employer hubs — an uncurated keyword URL renders for a human but
// 410s for a crawler, so building these client-side would risk publishing links into 410s.
//
// The employers row doubles as the listing's company filter (it replaced a multiselect that offered
// the same 20 employers), so a chip matching the URL's company segment renders selected and links
// back to the unfiltered listing. Employer links deliberately drop the category segment: an employer
// hub lists everything that employer has live, and category+employer is usually an empty intersection.
import { t as labelFor, defaultLanguage } from "../../common/jobLabels";

// Route params arrive still encoded ("F%26B"), and a malformed one must not throw in a computed.
function safeDecode(v) {
    const s = String(v || "");
    try {
        return decodeURIComponent(s);
    } catch (e) {
        return s;
    }
}

// A keyword reaches the URL as "joo%20koon" from a chip and as "joo-koon" from the search box, so
// compare on a flattened form or a chip never recognises its own page.
function norm(v) {
    return safeDecode(v).toLowerCase().replace(/[-_+]+/g, " ").replace(/\s+/g, " ").trim();
}

// One request per page, shared by every instance (the listing mounts two: employers up with the
// filters, areas/roles down with the related searches).
let cached = null;
function fetchLinks(axios) {
    if (!cached) {
        cached = axios
            .get(process.env.VUE_APP_SERVER_URL + "/api/quick-links") // GET: decryptPayload eats JSON POST bodies
            .then((r) => r.data || {})
            .catch(() => {
                cached = null; // let a later mount retry
                return {};
            });
    }
    return cached;
}

export default {
    name: "QuickSearchLinks",
    props: {
        // Which rows to render, in order. Split across two mount points on the listing.
        rows: {
            type: Array,
            default: () => ["roles", "areas", "categories", "employers"],
        },
        compact: { type: Boolean, default: false }, // sidebar width
        inline: { type: Boolean, default: false }, // label beside the chips, one row per group
        // Cap per row. Rows arrive sorted by live count, so this keeps the biggest ones.
        max: { type: Number, default: 0 },
        // Free-text search suggestions ("People also searched"). Rendered as buttons, not links —
        // the parent owns what a click does.
        suggestions: { type: Array, default: () => [] },
    },
    data() {
        return {
            areas: [],
            employers: [],
            categories: [],
            roles: [],
            expanded: {},
            language: defaultLanguage(false),
        };
    },
    computed: {
        limit() {
            if (this.max) return this.max;
            return this.compact ? 8 : 24;
        },
        visibleRows() {
            const defs = {
                roles: { key: "roles", label: "popular_searches", items: this.roles },
                areas: { key: "areas", label: "browse_by_area", items: this.areas },
                employers: { key: "employers", label: "employers_hiring", items: this.employers },
                categories: { key: "categories", label: "category", items: this.categories },
                suggestions: {
                    key: "suggestions",
                    label: "people_also_searched",
                    items: (this.suggestions || []).map((s) => ({ label: s })),
                },
            };
            return this.rows.map((k) => defs[k]).filter((r) => r && r.items.length);
        },
        // The company segment currently in the URL, lower-cased. "a--b" is the old multiselect form.
        selectedCompanies() {
            const seg = safeDecode(this.$route.params.company_id).toLowerCase();
            if (!seg || seg === "all-companies-jobs") return [];
            return seg.split("--").filter(Boolean);
        },
        selectedCategories() {
            const seg = safeDecode(this.$route.params.category_id).toLowerCase();
            if (!seg || seg === "all-categories-jobs") return [];
            return seg.split("--").map((s) => s.replace(/\s+/g, "")).filter(Boolean);
        },
        // A selected chip is its own off switch, and it clears everything: each row is a jump to one
        // clean view, which is what keeps the counts honest (the chip's number is exactly what the
        // click lands on). Refining within a filter is the keyword box's job.
        clearUrl() {
            return "/singapore-part-time-jobs";
        },
    },
    methods: {
        t(key) {
            return labelFor(this.language, key);
        },
        shown(row) {
            return this.expanded[row.key] ? row.items : row.items.slice(0, this.limit);
        },
        toggle(key) {
            this.$set(this.expanded, key, !this.expanded[key]);
        },
        isSelected(row, item) {
            if (row.key === "employers")
                return this.selectedCompanies.indexOf(String(item.alias || "").toLowerCase()) !== -1;
            if (row.key === "categories")
                return (
                    this.selectedCategories.indexOf(String(item.value || "").toLowerCase().replace(/\s+/g, "")) !== -1
                );
            // Areas are a geographic filter carried in the query string, not a keyword combo.
            if (row.key === "areas")
                return (
                    Boolean(item.area) &&
                    String(item.area).toLowerCase() === String(this.$route.query.area || "").toLowerCase()
                );
            // Popular searches are keyword combos, and a combo can be pinned to a category
            // (Cleaning/cleaner), so both halves of the URL have to match its own.
            if (row.key === "roles") {
                return (
                    Boolean(item.keyword) &&
                    norm(item.keyword) === norm(this.$route.params.keyword) &&
                    norm(item.category) === norm(this.$route.params.category_id || "all-categories-jobs")
                );
            }
            return false;
        },
    },
    created() {
        this.language = defaultLanguage(this.$country);
        fetchLinks(this.axios).then((d) => {
            this.areas = d.areas || [];
            this.employers = d.employers || [];
            this.categories = d.categories || [];
            this.roles = d.roles || [];
            // A selected chip that fell outside the top slice still has to be visible, or the filter
            // looks unset on the very page it is filtering.
            ["employers", "categories", "areas", "roles"].forEach((key) => {
                const row = this.visibleRows.find((r) => r.key === key);
                if (row && row.items.slice(this.limit).some((i) => this.isSelected(row, i))) {
                    this.$set(this.expanded, key, true);
                }
            });
        });
    },
};
</script>

<style scoped>
.quick-search {
    margin: 24px 0;
}
.quick-block {
    margin-bottom: 20px;
}
.quick-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
}
.quick-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.quick-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border: 1px solid #e4e7ec;
    border-radius: 16px;
    background: #fff;
    color: #4a4a4a;
    font-size: 13px;
    line-height: 1.5;
    text-decoration: none;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
}
button.quick-chip {
    font-family: inherit;
    cursor: pointer;
}
.quick-chip:hover {
    border-color: #4c8bf5;
    color: #4c8bf5;
    text-decoration: none;
}
.quick-chip.is-selected {
    background: #4c8bf5;
    border-color: #4c8bf5;
    color: #fff;
}
.quick-chip-count {
    font-size: 11px;
    color: #98a2b3;
}
.quick-chip.is-selected .quick-chip-count,
.quick-chip.is-selected .quick-chip-x {
    color: rgba(255, 255, 255, 0.85);
}
.quick-chip-x {
    font-size: 15px;
    line-height: 1;
}
.quick-more {
    padding: 5px 10px;
    border: 0;
    background: none;
    color: #4c8bf5;
    font-size: 13px;
    cursor: pointer;
}
.quick-more:hover {
    text-decoration: underline;
}
/* Inline: a browse strip that sits under a section heading — label on the left, chips flowing
   beside it, one compact row per group. */
.quick-search-inline {
    margin: 0 0 22px;
}
.quick-search-inline .quick-block {
    display: flex;
    align-items: baseline;
    gap: 14px;
    margin-bottom: 10px;
}
.quick-search-inline .quick-title {
    flex: 0 0 auto;
    min-width: 128px;
    margin-bottom: 0;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
}
.quick-search-inline .quick-chips {
    flex: 1 1 auto;
}
@media (max-width: 767px) {
    .quick-search-inline .quick-block {
        display: block;
    }
    .quick-search-inline .quick-title {
        margin-bottom: 6px;
    }
}
/* Sidebar: narrower column, so chips stay on one line each and the block sits tight to the
   widgets above it. */
.quick-search-compact {
    margin: 0;
}
.quick-search-compact .quick-block {
    margin-bottom: 18px;
}
.quick-search-compact .quick-title {
    font-size: 14px;
}
.quick-search-compact .quick-chip {
    font-size: 12px;
    padding: 4px 10px;
    max-width: 100%;
}
.quick-search-compact .quick-chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
